// lib_member wire contracts — the user+patron_profile fold (spec: one row per
// patron; staff live in the staff seam). Browser-safe zod only.
//
// Identity law: email/role/identifier/cardNumber/gradeLevel are CREATE-only —
// with ONE deliberate 0015 exception: groupId IS editable (membership is a
// policy assignment, not an identity fact) —
// UpdateMember simply has no such fields, and zod's default unknown-key
// stripping makes a client that sends them a silent no-op (source parity:
// UpdatePatron, patrons.schemas.ts:68-75).
import { z } from "zod";
import { CursorQuery, EntityId, EpochMs, Paise } from "@erp/core-schemas";

/** PATRON_ROLES verbatim (source patrons.schemas.ts:5). */
export const MEMBER_ROLES = ["student", "faculty", "staff", "guest", "research_scholar"] as const;
export const MemberRole = z.enum(MEMBER_ROLES);
export type MemberRole = z.infer<typeof MemberRole>;

export const MEMBER_STATUSES = ["active", "suspended"] as const;
export const MemberStatus = z.enum(MEMBER_STATUSES);
export type MemberStatus = z.infer<typeof MemberStatus>;

export const NOTIFICATION_CHANNELS = ["email", "sms", "whatsapp", "push"] as const;
export const NotificationChannel = z.enum(NOTIFICATION_CHANNELS);
export type NotificationChannel = z.infer<typeof NotificationChannel>;

/**
 * The extra_json ALLOWLIST (PATRON_EXTRA_KEYS, source patrons.service.ts:20-22).
 * Spread FLAT onto the wire shapes below (the spec pins the source's spread —
 * mappers.ts:22); the storage column is lib_member.extra_json. PATCH merges
 * these keys over stored ones, never wipes; unparseable stored JSON degrades
 * to {} — so every key is optional on read and write.
 */
export const MemberExtra = z
  .object({
    isMinor: z.boolean(),
    guardianName: z.string(),
    guardianRelation: z.string(),
    guardianPhone: z.string(),
    parentalProfile: z.string(),
    joinDate: z.string(), // free-text display string — ports verbatim
    branch: z.string(),
  })
  .partial();
export type MemberExtra = z.infer<typeof MemberExtra>;

/**
 * Derived circulation counters — computed per request from loans/fines
 * (list/detail/account always agree), never stored. finesDuePaise is the
 * source's `finesDue` renamed with the unit suffix: integer paise.
 */
export const MemberAccount = z.object({
  booksOut: z.number().int().min(0),
  overdue: z.number().int().min(0),
  finesDuePaise: Paise,
});
export type MemberAccount = z.infer<typeof MemberAccount>;

const memberRow = {
  id: EntityId, // new rows mint ULIDs; source 'usr-…' ids port verbatim
  userId: EntityId.nullable(), // opaque db-core identity — no FK, no cross-DB join; NULL for migrated rows
  displayName: z.string(),
  email: z.string(),
  image: z.string().nullable(),
  role: MemberRole,
  /** 0015 — the member's patron group (real reference). NULL = unmigrated/role-derived; group_id WINS over role wherever both could answer. */
  groupId: EntityId.nullable(),
  identifier: z.string(), // ←patron_profile.student_id_number
  cardNumber: z.string().nullable(), // 'LIB-XXXXXX'
  gradeLevel: z.string().nullable(),
  department: z.string().nullable(),
  parentEmail: z.string().nullable(),
  status: MemberStatus,
  primaryNotificationChannel: NotificationChannel.nullable(),
  secondaryNotificationChannel: NotificationChannel.nullable(), // dead in source — never written
  phone: z.string().nullable(),
  address: z.string().nullable(),
  notes: z.string().nullable(),
  ...MemberExtra.shape, // spread back from extra_json — present only when stored
  createdAt: EpochMs,
  updatedAt: EpochMs,
};

/** The full member DTO: row columns + the derived account counters. */
export const Member = z.object({ ...memberRow, ...MemberAccount.shape });
export type Member = z.infer<typeof Member>;

export const CreateMember = z.object({
  displayName: z.string().trim().min(1).max(120),
  email: z.email().max(254),
  role: MemberRole,
  /** Optional at create — omitted or null means NULL (policy falls back to the role-derived canonical group). Must name an existing group when sent. */
  groupId: EntityId.nullable().optional(),
  identifier: z.string().trim().min(1).max(64),
  /** Omit to auto-generate 'LIB-XXXXXX' (regenerated on collision — retry loop, not a 400). */
  cardNumber: z.string().trim().min(1).max(64).optional(),
  gradeLevel: z.string().optional(),
  department: z.string().optional(),
  parentEmail: z.email().optional(),
  primaryNotificationChannel: NotificationChannel.optional(),
  image: z.string().optional(),
  phone: z.string().optional(),
  address: z.string().optional(),
  notes: z.string().optional(),
  ...MemberExtra.shape, // flat, per the pinned source body shape
});
export type CreateMember = z.infer<typeof CreateMember>;

/** Identity fields (email/role/identifier/cardNumber/gradeLevel) are absent by design. */
export const UpdateMember = z
  .object({
    displayName: z.string().trim().min(1).max(120),
    /**
     * 0015 — THE editable group (deliberate, narrow loosening of the identity
     * law for group ONLY; email/role/identifier/cardNumber stay create-only).
     * Must name an existing group; null clears back to role-derived.
     */
    groupId: EntityId.nullable(),
    department: z.string(),
    status: MemberStatus, // suspend/reactivate rides PATCH {status}
    parentEmail: z.email(),
    primaryNotificationChannel: NotificationChannel,
    image: z.string(),
    phone: z.string(),
    address: z.string(),
    notes: z.string(),
  })
  .partial()
  // allowlisted extra keys ride flat and MERGE over stored extra_json server-side
  .extend(MemberExtra.shape);
export type UpdateMember = z.infer<typeof UpdateMember>;

/** Keyset list on (display_name, id) — search ORs LIKE over name/email/card/identifier (capped: search text is a filter, not a document). */
export const MemberListQuery = CursorQuery.extend({
  search: z.string().max(200).optional(),
  role: MemberRole.optional(),
  status: MemberStatus.optional(),
});
export type MemberListQuery = z.infer<typeof MemberListQuery>;
